export const languages = [
  {
    id: 'ja',
    name: 'Japanese',
    nativeName: '日本語',
    available: true,
    capabilities: {reading: true, characters: true},
    definitionUrl: './languages/ja.json',
  },
];

const emptyPrograms = () => ({version: 1, programs: []});

export function availableLanguages() {
  return languages.filter(language => language.available);
}

async function fetchJson(fetcher, url, label) {
  const response = await fetcher(url);
  if (!response?.ok) throw new Error(`${label} unavailable (${response?.status ?? 'network error'})`);
  return response.json();
}

async function loadOptionalPrograms(fetcher, url) {
  if (!url) return emptyPrograms();
  try {
    const catalog = await fetchJson(fetcher, url, 'Programs');
    if (Array.isArray(catalog)) return {version: 1, programs: catalog};
    if (!catalog || !Array.isArray(catalog.programs)) throw new Error('Invalid programs catalog');
    return {version: catalog.version ?? 1, ...catalog, programs: catalog.programs};
  } catch {
    return emptyPrograms();
  }
}

export async function loadLanguage(id, {fetcher = fetch} = {}) {
  const registered = languages.find(language => language.id === id);
  if (!registered?.available) throw new Error(`Language unavailable: ${id}`);

  const rawDefinition = await fetchJson(fetcher, registered.definitionUrl, 'Language definition');
  if (rawDefinition?.id !== id) throw new Error(`Invalid language definition for ${id}`);
  const definition = {
    ...registered,
    ...rawDefinition,
    capabilities: {...registered.capabilities, ...rawDefinition.capabilities},
  };
  const content = definition.content ?? {};
  if (!content.deck || !content.stories || !content.manifest) {
    throw new Error(`Language definition ${id} is missing required content URLs`);
  }

  const [rawDeck, stories, manifest, programs] = await Promise.all([
    fetchJson(fetcher, content.deck, 'Deck'),
    fetchJson(fetcher, content.stories, 'Stories'),
    fetchJson(fetcher, content.manifest, 'Audio manifest'),
    loadOptionalPrograms(fetcher, content.programs),
  ]);
  if (!Array.isArray(rawDeck) || !Array.isArray(stories)) throw new Error(`Invalid content for ${id}`);

  const deck = rawDeck.map(word => {
    const configuredTarget = definition.targetDefinitionField
      ? word[definition.targetDefinitionField]
      : undefined;
    return {
      ...word,
      targetDefinition: word.targetDefinition ?? configuredTarget ?? word.definition ?? word.ja ?? word.meaning,
    };
  });
  const lessons = Array.isArray(definition.lessons) ? structuredClone(definition.lessons) : [];
  return {definition, deck, stories, manifest, lessons, programs};
}
